import axios from 'axios';
import { newIdempotencyKey } from '@immoaktas/api-client';
import type { CostCategory } from '@immoaktas/shared';
import { api } from './api';
import i18n, { UserError } from '../i18n';

export async function sha256Hex(file: Blob): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const MIME: Record<string, string> = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', webp: 'image/webp', csv: 'text/csv', xml: 'application/xml' };
export const mimeOf = (f: File) => f.type || MIME[f.name.split('.').pop()?.toLowerCase() ?? ''] || 'application/octet-stream';

/** 03 §4.3: register (hash, duplicate check) → PUT to R2 with signed URL → confirm upload. */
export async function uploadDocument(file: File, opts: { propertyId?: string; kind?: string; uploadHint?: { category?: CostCategory; serviceFrom?: string; serviceTo?: string; heatingImport?: boolean }; onProgress?: (p: number) => void }) {
  if (file.size > 25 * 1024 * 1024) throw new UserError(i18n.t('upload.tooLarge', { mb: 25 }));
  const sha256 = await sha256Hex(file);
  const mime = mimeOf(file);
  const reg = await api.documents.create({ ...(opts.propertyId ? { propertyId: opts.propertyId } : {}), kind: (opts.kind ?? 'invoice') as any, channel: 'web', mime: mime as any, size: file.size, sha256, originalName: file.name, ...(opts.uploadHint && Object.values(opts.uploadHint).some(Boolean) ? { uploadHint: opts.uploadHint } : {}) }, newIdempotencyKey());
  if (reg.duplicate) return { documentId: reg.documentId, duplicate: true };
  if (reg.uploadUrl) {
    // a blocked PUT (CORS, network, expired link) must not look like "KI liest": the receipt stays without file
    await axios.put(reg.uploadUrl, file, { headers: reg.uploadHeaders ?? { 'Content-Type': mime }, onUploadProgress: (e) => opts.onProgress?.(e.total ? e.loaded / e.total : 0) }).catch((err: unknown) => {
      console.warn('upload PUT failed', err);
      throw new UserError(i18n.t('upload.putFailed'));
    });
  }
  await api.documents.uploaded(reg.documentId);
  return { documentId: reg.documentId, duplicate: false };
}

export function downloadBlob(data: ArrayBuffer | string, name: string, type = 'application/pdf') {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
export function openBlob(data: ArrayBuffer, type = 'application/pdf') {
  const url = URL.createObjectURL(new Blob([data], { type }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
/** Opens the print dialog for a PDF via a hidden frame; falls back to a new tab where the browser cannot print frames. */
export function printBlob(data: ArrayBuffer, type = 'application/pdf') {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.src = url;
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      window.open(url, '_blank', 'noopener');
    }
  };
  document.body.appendChild(frame);
  setTimeout(() => {
    frame.remove();
    URL.revokeObjectURL(url);
  }, 120_000);
}
